import React from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import CurrencyFormat from 'react-currency-format'
import RewardAdsStatusTag from './RewardAdsStatusTag'
import RewardAdsRowActions from './RewardAdsRowActions'

function Checkbox({ checked, onChange }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border border-border-lines accent-primary"
    />
  )
}

export default withTranslation()(function RewardAdsTable({
  t, campaigns = [], hasAnyCampaigns, selected, onToggleSelect, onCreate,
  onViewDetails, onEdit, onDuplicate, onViewAnalytics, onEndCampaign
}) {
  const columns = t('rewardAds.listing.columns', { returnObjects: true })

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[900px]">
        <div className="flex items-center bg-background border-b border-border-lines">
          <div className="w-10 shrink-0" />
          <div className="flex-1 min-w-[220px] px-2 py-2.5 text-sm font-semibold text-dark-blue">{columns.campaign}</div>
          <div className="w-[160px] shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue flex items-center gap-1">{columns.period} <UIcons.UilSort size={14} /></div>
          <div className="w-[140px] shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue flex items-center gap-1">{columns.budget} <UIcons.UilSort size={14} /></div>
          <div className="w-[140px] shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue">{columns.targeting}</div>
          <div className="w-[130px] shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue flex items-center gap-1">{columns.frequency} <UIcons.UilSort size={14} /></div>
          <div className="w-[110px] shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue">{columns.status}</div>
          <div className="w-12 shrink-0" />
        </div>

        {
          !hasAnyCampaigns && (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-primary bg-opacity-10 flex items-center justify-center">
                <UIcons.UilShieldCheck size={40} className="text-primary" />
              </div>
              <p className="text-base font-semibold text-dark-blue">{t('rewardAds.listing.empty_title')}</p>
              <p className="text-sm text-secondary max-w-sm">{t('rewardAds.listing.empty_hint')}</p>
              <button
                type="button"
                onClick={onCreate}
                className="mt-2 flex items-center gap-1.5 bg-primary text-white rounded-full h-9 px-4 text-sm font-medium"
              >
                <UIcons.UilPlus size={16} /> {t('rewardAds.create')}
              </button>
            </div>
          )
        }

        {
          hasAnyCampaigns && campaigns.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-14 text-center">
              <p className="text-base font-semibold text-dark-blue">{t('rewardAds.listing.no_results_title')}</p>
              <p className="text-sm text-secondary max-w-sm">{t('rewardAds.listing.no_results_hint')}</p>
            </div>
          )
        }

        {
          campaigns.map((campaign) => (
            <div key={campaign.id} className="flex items-center border-b border-border-lines h-24">
              <div className="w-10 shrink-0 flex items-center justify-center">
                <Checkbox checked={!!selected[campaign.id]} onChange={() => onToggleSelect(campaign.id)} />
              </div>

              <div className="flex-1 min-w-[220px] flex items-center gap-3 px-2">
                <div className={`w-12 h-12 rounded-lg bg-${campaign.thumbnailTone} flex items-center justify-center shrink-0 overflow-hidden`}>
                  <UIcons.UilPlayCircle size={20} className="text-white" />
                </div>
                <p className="text-sm text-dark-blue truncate">{campaign.title}</p>
              </div>

              <div className="w-[160px] shrink-0 px-2">
                <p className="text-sm text-dark-blue whitespace-nowrap">{campaign.period.label}</p>
                <p className="text-xs text-secondary">{campaign.period.daysLeft} days left</p>
              </div>

              <div className="w-[140px] shrink-0 px-2">
                <CurrencyFormat
                  value={campaign.budget}
                  displayType="text"
                  thousandSeparator
                  prefix="NGN "
                  renderText={(value) => <p className="text-sm text-dark-blue whitespace-nowrap">{value}</p>}
                />
                <p className="text-xs text-secondary whitespace-nowrap">Daily: NGN {campaign.dailyBudget.toLocaleString()}</p>
              </div>

              <div className="w-[140px] shrink-0 px-2">
                <p className="text-sm text-dark-blue">{campaign.targeting.audience}</p>
                <p className="text-xs text-secondary">{campaign.targeting.age}</p>
              </div>

              <div className="w-[130px] shrink-0 px-2">
                <p className="text-sm text-dark-blue whitespace-nowrap">{campaign.frequency.perUser}</p>
                {campaign.frequency.per && <p className="text-xs text-secondary">Per {campaign.frequency.per}</p>}
              </div>

              <div className="w-[110px] shrink-0 px-2">
                <RewardAdsStatusTag status={campaign.status} />
              </div>

              <div className="w-12 shrink-0 flex items-center justify-center">
                <RewardAdsRowActions
                  campaign={campaign}
                  onViewDetails={onViewDetails}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onViewAnalytics={onViewAnalytics}
                  onEndCampaign={onEndCampaign}
                />
              </div>
            </div>
          ))
        }
      </div>
    </div>
  )
})
